import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { VotingComponentProps, VOTE_VALUES, VoteValue } from '@/types/feedback';

const VotingComponent: React.FC<VotingComponentProps> = ({
  feedbackId,
  initialVoteCount,
  initialUserVote = 0,
  disabled = false,
  size = 'md',
}) => {
  const [voteCount, setVoteCount] = useState(initialVoteCount);
  const [userVote, setUserVote] = useState<VoteValue>(initialUserVote as VoteValue);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Size variants
  const sizeVariants = {
    sm: {
      button: 'h-6 w-6 p-0',
      icon: 'h-3 w-3',
      text: 'text-xs',
      container: 'gap-1',
    },
    md: {
      button: 'h-8 w-8 p-0',
      icon: 'h-4 w-4',
      text: 'text-sm',
      container: 'gap-1',
    },
    lg: {
      button: 'h-10 w-10 p-0',
      icon: 'h-5 w-5',
      text: 'text-base',
      container: 'gap-2',
    },
  };

  const variant = sizeVariants[size];

  const handleVote = useCallback(async (voteValue: VoteValue) => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    const previousVoteCount = voteCount;
    const previousUserVote = userVote;

    try {
      // Optimistic update
      const voteDiff = voteValue - userVote;
      setVoteCount(prev => prev + voteDiff);
      setUserVote(voteValue);

      // Call the database function
      const { data, error } = await supabase.rpc('cast_feedback_vote', {
        p_feedback_id: feedbackId,
        p_vote_value: voteValue,
      });

      if (error) throw error;

      if (!data[0]?.success) {
        throw new Error(data[0]?.message || 'Failed to cast vote');
      }

      // Update with actual server values
      setVoteCount(data[0].new_vote_count);
      setUserVote(data[0].user_vote);

      // Show success message only for first-time votes
      if (previousUserVote === VOTE_VALUES.NEUTRAL) {
        toast({
          title: 'Vote cast',
          description: data[0].message,
        });
      }
    } catch (error: any) {
      // Rollback optimistic update
      setVoteCount(previousVoteCount);
      setUserVote(previousUserVote);

      toast({
        title: 'Failed to vote',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [feedbackId, voteCount, userVote, disabled, isLoading, toast]);

  const handleUpvote = useCallback(() => {
    const newVote = userVote === VOTE_VALUES.UPVOTE ? VOTE_VALUES.NEUTRAL : VOTE_VALUES.UPVOTE;
    handleVote(newVote);
  }, [userVote, handleVote]);

  const handleDownvote = useCallback(() => {
    const newVote = userVote === VOTE_VALUES.DOWNVOTE ? VOTE_VALUES.NEUTRAL : VOTE_VALUES.DOWNVOTE;
    handleVote(newVote);
  }, [userVote, handleVote]);

  return (
    <div className={cn('flex flex-col items-center', variant.container)}>
      {/* Upvote Button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          variant.button,
          'transition-colors duration-200',
          userVote === VOTE_VALUES.UPVOTE
            ? 'text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100'
            : 'text-muted-foreground hover:text-green-600 hover:bg-green-50',
          disabled && 'opacity-50 cursor-not-allowed',
          isLoading && 'animate-pulse'
        )}
        onClick={handleUpvote}
        disabled={disabled || isLoading}
        aria-label={userVote === VOTE_VALUES.UPVOTE ? 'Remove upvote' : 'Upvote'}
      >
        <ChevronUp className={variant.icon} />
      </Button>

      {/* Vote Count */}
      <span
        className={cn(
          'font-medium transition-colors duration-200',
          variant.text,
          userVote === VOTE_VALUES.UPVOTE && 'text-green-600',
          userVote === VOTE_VALUES.DOWNVOTE && 'text-red-600',
          userVote === VOTE_VALUES.NEUTRAL && 'text-muted-foreground'
        )}
      >
        {voteCount}
      </span>

      {/* Downvote Button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          variant.button,
          'transition-colors duration-200',
          userVote === VOTE_VALUES.DOWNVOTE
            ? 'text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100'
            : 'text-muted-foreground hover:text-red-600 hover:bg-red-50',
          disabled && 'opacity-50 cursor-not-allowed',
          isLoading && 'animate-pulse'
        )}
        onClick={handleDownvote}
        disabled={disabled || isLoading}
        aria-label={userVote === VOTE_VALUES.DOWNVOTE ? 'Remove downvote' : 'Downvote'}
      >
        <ChevronDown className={variant.icon} />
      </Button>
    </div>
  );
};

export default VotingComponent;